/**
 * src/utils/eduZonedTime.ts
 *
 * Workspace-timezone date/time utilities for School Events.
 */

export const DEFAULT_EDU_TIMEZONE = 'Asia/Kolkata'

export function resolveWorkspaceTimezone(
  workspace?: { timezone?: string } | null,
  fallback = DEFAULT_EDU_TIMEZONE,
): string {
  const tz = workspace?.timezone?.trim()
  if (!tz) return fallback
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return tz
  } catch {
    return fallback
  }
}

function getZonedOffsetMs(utcDate: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  const parts = formatter.formatToParts(utcDate)
  const m: Record<string, string> = {}
  for (const p of parts) m[p.type] = p.value
  const wallUtcMs = Date.UTC(
    parseInt(m.year, 10),
    parseInt(m.month, 10) - 1,
    parseInt(m.day, 10),
    parseInt(m.hour, 10),
    parseInt(m.minute, 10),
    parseInt(m.second, 10),
  )
  return wallUtcMs - utcDate.getTime()
}

export function zonedWallClockToUtcIso(
  date: string,
  time: string,
  timeZone = DEFAULT_EDU_TIMEZONE,
): string {
  const tz = timeZone?.trim() || DEFAULT_EDU_TIMEZONE
  const [y, mo, d] = date.split('-').map((v) => parseInt(v, 10))
  const [h, mi] = time.split(':').map((v) => parseInt(v, 10))
  const desiredWallUtc = Date.UTC(y, mo - 1, d, h, mi, 0, 0)

  // Initial estimate
  let utcMs = desiredWallUtc
  const offset1 = getZonedOffsetMs(new Date(utcMs), tz)
  utcMs = desiredWallUtc - offset1

  // Refine offset at computed UTC (handles DST shift boundaries)
  const offsetRefined = getZonedOffsetMs(new Date(utcMs), tz)
  utcMs = desiredWallUtc - offsetRefined

  return new Date(utcMs).toISOString()
}

export function utcIsoToZonedParts(
  iso: string,
  timeZone = DEFAULT_EDU_TIMEZONE,
): { date: string; time: string } {
  const tz = timeZone?.trim() || DEFAULT_EDU_TIMEZONE
  const d = new Date(iso)
  if (isNaN(d.getTime())) {
    return {
      date: iso.slice(0, 10),
      time: iso.slice(11, 16) || '00:00',
    }
  }

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  const parts = formatter.formatToParts(d)
  const m: Record<string, string> = {}
  for (const p of parts) m[p.type] = p.value
  return {
    date: `${m.year}-${m.month}-${m.day}`,
    time: `${m.hour}:${m.minute}`,
  }
}
